import React from 'react';
import { Zap, Flame, Droplets, ArrowUpRight, Check, FileText, Landmark, Search, Bell, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

/* Browser/app window chrome wrapper */
function AppFrame({ children, title = 'app.billportal.io', className }) {
  return (
    <div className={cn('overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 dark:border-white/10 dark:bg-[#101015] dark:shadow-black/40', className)}>
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-white/10 dark:bg-white/[0.03]">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        <div className="mx-auto flex items-center gap-1.5 rounded-md bg-white px-3 py-1 text-[11px] text-slate-400 ring-1 ring-slate-200 dark:bg-white/5 dark:text-slate-500 dark:ring-white/10">
          <ShieldCheck className="h-3 w-3 text-brand" /> {title}
        </div>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

function Kpi({ label, value, sub, tone = 'slate' }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.02]">
      <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</div>
      <div className="mt-1 font-display text-lg font-bold text-slate-900 dark:text-white">{value}</div>
      {sub && (
        <div className={cn('mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-medium', tone === 'brand' ? 'text-brand' : 'text-emerald-500')}>
          <ArrowUpRight className="h-3 w-3" /> {sub}
        </div>
      )}
    </div>
  );
}

function BarChart() {
  const bars = [38, 52, 44, 61, 49, 72, 58, 80, 67, 90, 76, 84];
  const max = Math.max(...bars);
  return (
    <div className="flex h-28 items-end gap-1.5">
      {bars.map((b, i) => (
        <div key={i} className="flex-1 rounded-t-sm bg-gradient-to-t from-brand/30 to-brand transition-all" style={{ height: `${(b / max) * 100}%` }} />
      ))}
    </div>
  );
}

function Donut() {
  // electricity 52, gas 26, water 22
  const r = 32; const c = 2 * Math.PI * r;
  const segs = [
    { v: 52, color: '#FF5757' },
    { v: 26, color: '#FF9F43' },
    { v: 22, color: '#38bdf8' },
  ];
  let offset = 0;
  return (
    <svg viewBox="0 0 90 90" className="h-24 w-24 -rotate-90">
      <circle cx="45" cy="45" r={r} fill="none" stroke="currentColor" strokeWidth="11" className="text-slate-100 dark:text-white/10" />
      {segs.map((s, i) => {
        const len = (s.v / 100) * c;
        const dash = `${len} ${c - len}`;
        const el = <circle key={i} cx="45" cy="45" r={r} fill="none" stroke={s.color} strokeWidth="11" strokeDasharray={dash} strokeDashoffset={-offset} />;
        offset += len;
        return el;
      })}
    </svg>
  );
}

function DashboardPreview() {
  const rows = [
    { p: 'Riverside Apartments', v: 'City Electric', c: Zap, a: '$4,210.55', s: 'Paid' },
    { p: 'Maple Court', v: 'Metro Water', c: Droplets, a: '$1,884.20', s: 'In PF' },
    { p: 'Harbor Lofts', v: 'Regional Gas', c: Flame, a: '$2,765.00', s: 'Review' },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-sm font-bold text-slate-900 dark:text-white">Bills Dashboard</div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500">Portfolio · last 12 months</div>
        </div>
        <div className="hidden items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] text-slate-400 dark:border-white/10 dark:text-slate-500 sm:flex">
          <Search className="h-3 w-3" /> Filter
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kpi label="Total spend" value="$30.5M" sub="+4.2%" tone="brand" />
        <Kpi label="Bills" value="22,741" sub="+1,204" />
        <Kpi label="Properties" value="312" />
        <Kpi label="Paid" value="98.6%" sub="on time" />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.02] sm:col-span-2">
          <div className="mb-2 text-[11px] font-medium text-slate-500 dark:text-slate-400">Monthly spend</div>
          <BarChart />
        </div>
        <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.02]">
          <div className="mb-1 self-start text-[11px] font-medium text-slate-500 dark:text-slate-400">By commodity</div>
          <Donut />
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
        {rows.map((r, i) => (
          <div key={i} className={cn('flex items-center justify-between px-3 py-2 text-xs', i !== rows.length - 1 && 'border-b border-slate-100 dark:border-white/5')}>
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand/10 text-brand"><r.c className="h-3.5 w-3.5" /></span>
              <div>
                <div className="font-medium text-slate-800 dark:text-slate-100">{r.p}</div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500">{r.v}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-semibold text-slate-800 dark:text-slate-100">{r.a}</span>
              <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium',
                r.s === 'Paid' ? 'bg-emerald-500/10 text-emerald-500' : r.s === 'Review' ? 'bg-amber-500/10 text-amber-500' : 'bg-brand/10 text-brand')}>{r.s}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentsPreview() {
  const rows = [
    { v: 'City Electric Co.', acct: '••• 4821', a: '$4,210.55' },
    { v: 'Metro Water Dept.', acct: '••• 7733', a: '$1,884.20' },
    { v: 'Regional Gas LLC', acct: '••• 9920', a: '$2,765.00' },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-sm font-bold text-slate-900 dark:text-white">Payment File · PF-2381</div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500">12 invoices · GL allocated</div>
        </div>
        <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-500">Approved</span>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
        {rows.map((r, i) => (
          <div key={i} className={cn('flex items-center justify-between px-3 py-2.5 text-xs', i !== rows.length - 1 && 'border-b border-slate-100 dark:border-white/5')}>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 rounded bg-brand/10 p-0.5 text-brand" />
              <div>
                <div className="font-medium text-slate-800 dark:text-slate-100">{r.v}</div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500">Acct {r.acct}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:border-white/10 dark:text-slate-400">ACH · CCD+</span>
              <span className="font-semibold text-slate-800 dark:text-slate-100">{r.a}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5 dark:bg-white/[0.03]">
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400"><Landmark className="h-4 w-4 text-brand" /> Funds via banking partner</div>
        <div className="text-right">
          <div className="text-[10px] text-slate-400 dark:text-slate-500">Total requested</div>
          <div className="font-display text-base font-bold text-slate-900 dark:text-white">$8,859.75</div>
        </div>
      </div>
    </div>
  );
}

function ParsingPreview() {
  const fields = [
    { k: 'Vendor', v: 'City Electric Co.', conf: '0.99' },
    { k: 'Account #', v: '08423-43000-1', conf: '0.98' },
    { k: 'Service period', v: 'May 1 – May 31', conf: '0.97' },
    { k: 'Usage (kWh)', v: '14,820', conf: '0.96' },
    { k: 'Amount due', v: '$4,210.55', conf: '0.99' },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-display text-sm font-bold text-slate-900 dark:text-white">AI bill parsing</div>
        <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 text-[11px] font-semibold text-brand"><Check className="h-3 w-3" /> Validated</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/[0.02]">
          <div className="mb-2 flex items-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500"><FileText className="h-3 w-3" /> invoice.pdf · p.1</div>
          <div className="space-y-1.5">
            {[80, 60, 95, 45, 70, 88, 55, 72].map((w, i) => (
              <div key={i} className="h-1.5 rounded-full bg-slate-100 dark:bg-white/10" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          {fields.map((f, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] dark:border-white/10 dark:bg-white/[0.02]">
              <div>
                <div className="text-[9px] uppercase tracking-wide text-slate-400 dark:text-slate-500">{f.k}</div>
                <div className="font-medium text-slate-800 dark:text-slate-100">{f.v}</div>
              </div>
              <span className="inline-flex items-center gap-0.5 text-emerald-500"><Check className="h-3 w-3" />{f.conf}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ProductPreview({ variant = 'dashboard', title, className }) {
  return (
    <AppFrame title={title} className={className}>
      {variant === 'payments' && <PaymentsPreview />}
      {variant === 'parsing' && <ParsingPreview />}
      {variant === 'dashboard' && <DashboardPreview />}
    </AppFrame>
  );
}
